const SOCIAL_ICONS = {
  twitter: "ph-x-logo",
  facebook: "ph-facebook-logo",
  instagram: "ph-instagram-logo",
};

const KIND_COLOR_MAP = {
  "Blog": "bg-blue-100 text-blue-700",
  "X post": "bg-zinc-900 text-white",
  "Website": "bg-emerald-100 text-emerald-700",
};

function safeHttpUrl(value, { defaultProtocol = true } = {}) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw)
    ? raw
    : (defaultProtocol ? `https://${raw}` : raw);
  try {
    const parsed = new URL(candidate);
    return ["http:", "https:"].includes(parsed.protocol) ? parsed.href : null;
  } catch (_err) {
    return null;
  }
}

function canParseUrl(candidate) {
  return typeof URL !== "undefined"
    && typeof URL.canParse === "function"
    && URL.canParse(candidate);
}

function parsedUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;

  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`;
  return canParseUrl(candidate) ? new URL(candidate) : null;
}

function isKnownSocialHost(hostname) {
  const host = String(hostname || "").replace(/^www\./i, "").toLowerCase();
  return ["instagram.com", "x.com", "twitter.com", "facebook.com", "fb.com"]
    .some(domain => host === domain || host.endsWith(`.${domain}`));
}

function socialHandleFromSegment(segment) {
  const handle = String(segment || "").trim().replace(/^@+/, "");
  return handle ? `@${handle}` : "";
}

function socialDisplayHandle(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const parsed = parsedUrl(raw);
  if (parsed && isKnownSocialHost(parsed.hostname)) {
    const segment = parsed.pathname.split("/").map(s => s.trim()).filter(Boolean)[0];
    const handle = socialHandleFromSegment(segment);
    if (handle) return handle;
  }

  const cleaned = raw
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/[?#].*$/, "")
    .replace(/\/+$/, "");
  const match = cleaned.match(/^(?:[^/]+\.)*(?:instagram\.com|x\.com|twitter\.com|facebook\.com|fb\.com)\/([^/]+)/i);
  if (match) {
    const handle = socialHandleFromSegment(match[1]);
    if (handle) return handle;
  }

  return raw;
}

function xProfileUrlFromHandle(handle) {
  const display = socialDisplayHandle(handle);
  const clean = String(display || "").trim().replace(/^@+/, "");
  return clean ? `https://x.com/${clean}` : "";
}

// Compact currency formatter: "$1.2B" / "$45M" / "$120K" / "$840".
function formatCurrencyShort(n) {
  if (typeof n !== "number" || !isFinite(n)) return "";
  const abs = Math.abs(n);
  if (abs >= 1e12) return `$${(n / 1e12).toFixed(1).replace(/\.0$/, "")}T`;
  if (abs >= 1e9)  return `$${(n / 1e9).toFixed(1).replace(/\.0$/, "")}B`;
  if (abs >= 1e6)  return `$${(n / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
  if (abs >= 1e3)  return `$${(n / 1e3).toFixed(1).replace(/\.0$/, "")}K`;
  return `$${Math.round(n)}`;
}

function formatNumberFull(n) {
  if (typeof n !== "number" || !isFinite(n)) return "";
  return n.toLocaleString();
}

function formatYear(n) {
  if (typeof n === "number" && isFinite(n)) return String(Math.trunc(n));
  return String(n);
}

function formatDateRelative(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  const now = Date.now();
  const diff = now - d.getTime();
  const days = Math.floor(diff / 86400000);
  if (days < 1)   return "today";
  if (days < 30)  return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

const FORMATTERS = {
  logo: (_v, row) => {
    const domain = String(row.domain || "").replace(/[^\w.-]/g, "");
    return (
      <img
        src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`}
        className="w-5 h-5 rounded object-contain"
        alt=""
        onError={(e) => { e.target.style.visibility = "hidden"; }}
      />
    );
  },

  domain: (v) => <span className="font-mono text-xs text-zinc-600">{v}</span>,

  url: (v) => {
    if (!v) return <span className="text-zinc-300">-</span>;
    const raw = String(v);
    const href = safeHttpUrl(raw);
    if (!href) {
      return <span className="truncate inline-block max-w-full" title={raw}>{raw}</span>;
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={raw}
        className="inline-flex max-w-full min-w-0 items-center gap-1.5 rounded px-1 py-0.5 text-zinc-600 hover:bg-zinc-100 hover:text-purple-700 transition-colors"
      >
        <i className="ph ph-link text-sm shrink-0"></i>
        <span className="truncate font-mono text-xs">{raw}</span>
      </a>
    );
  },

  text: (v) => <span className="truncate inline-block max-w-full" title={String(v)}>{String(v)}</span>,

  number: (v) => <span className="tabular-nums">{formatNumberFull(v)}</span>,

  year: (v) => <span className="tabular-nums">{formatYear(v)}</span>,

  currency: (v) => (
    <span className="tabular-nums" title={typeof v === "number" ? `$${formatNumberFull(v)}` : String(v)}>
      {formatCurrencyShort(v)}
    </span>
  ),

  date: (v) => {
    if (!v) return <span className="text-zinc-300">-</span>;
    const abs = String(v);
    return (
      <span className="text-xs text-zinc-700" title={abs}>
        {formatDateRelative(v)}
      </span>
    );
  },

  image: (v) => {
    if (!v) return <span className="text-zinc-300">-</span>;
    const src = safeHttpUrl(v, { defaultProtocol: false });
    if (!src) return <span className="text-zinc-300">-</span>;
    return (
      <img
        src={src}
        className="w-10 h-6 object-cover rounded"
        alt=""
        onError={(e) => { e.target.style.visibility = "hidden"; }}
      />
    );
  },

  chip_list: (v, _row, col) => {
    const list = Array.isArray(v) ? v : (v ? [String(v)] : []);
    if (list.length === 0) return <span className="text-zinc-300">-</span>;
    const noun = (col.label || "items").toLowerCase();
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded bg-zinc-100 text-zinc-700"
        title={list.join(", ")}
      >
        <i className="ph ph-tag-chevron text-[10px]"></i>
        {list.length} {noun}
      </span>
    );
  },

  social_icon: (v, _row, col) => {
    if (!v) return <span className="text-zinc-300">-</span>;
    const href = safeHttpUrl(v);
    if (!href) return <span className="truncate inline-block max-w-full" title={String(v)}>{String(v)}</span>;
    const iconName = SOCIAL_ICONS[col.platform] || "ph-share-network";
    const displayHandle = socialDisplayHandle(v);
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={String(v)}
        className="inline-flex max-w-full min-w-0 items-center gap-1.5 rounded px-1 py-0.5 text-zinc-600 hover:bg-zinc-100 hover:text-purple-700 transition-colors"
      >
        <i className={`ph ${iconName} text-base shrink-0`}></i>
        <span className="min-w-0 truncate font-mono text-xs">{displayHandle}</span>
      </a>
    );
  },

  kind_badge: (v) => (
    <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded ${KIND_COLOR_MAP[v] || "bg-zinc-100 text-zinc-700"}`}>
      {String(v)}
    </span>
  ),
};

const KEY_FORMATTERS = {
  work_email: (v) => <span className="font-mono text-xs text-zinc-700">{v}</span>,
  competition_reason: (v) => (
    <span className="text-xs text-zinc-700">
      {String(v || "").replace(/_/g, " ") || "-"}
    </span>
  ),
  similar_to: (v, row) => {
    const href = safeHttpUrl(row?.similar_to_profile_href, { defaultProtocol: false });
    const label = String(v || "");
    if (!href) {
      return <span className="truncate inline-block max-w-full" title={label}>{label}</span>;
    }
    return (
      <a
        href={href}
        className="no-btn-shadow inline-flex max-w-full min-w-0 items-center gap-1.5 rounded px-1 py-0.5 text-zinc-700 hover:bg-zinc-100 hover:text-purple-700 transition-colors"
        title={label}
      >
        <i className="ph ph-user text-sm shrink-0"></i>
        <span className="truncate">{label}</span>
      </a>
    );
  },
};

export function Cell({ row, col }) {
  const shimmering = (row._shimmerCols || []).includes(col.key);

  if (col.public_only && row.company_type && row.company_type !== "PUBLIC_COMPANY") {
    return <span className="text-zinc-300">-</span>;
  }

  if (col.type === "logo" || col.key === "logo") {
    return FORMATTERS.logo(null, row, col);
  }

  let value = row[col.key];
  if (col.key === "x_profile_url" && !value && row.x_handle) {
    value = xProfileUrlFromHandle(row.x_handle);
  }
  if (
    shimmering
    && (value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0))
  ) {
    return <span className="prospector-shimmer" style={{ width: 80 }}>...</span>;
  }
  if (value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) {
    return <span className="text-zinc-300">-</span>;
  }

  const keyFmt = KEY_FORMATTERS[col.key];
  if (keyFmt) return keyFmt(value, row, col);

  const fmt = FORMATTERS[col.type] || FORMATTERS.text;
  return fmt(value, row, col);
}
